import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Icon from "@/components/ui/Icon";
import { useThemeSettings } from '@/contexts/ThemeContext';
import { MapPin, Star, Clock } from 'lucide-react';

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '??';
  const trimmed = name.trim();
  
  if (trimmed.length <= 1) return '??';
  if (trimmed.toLowerCase() === 'child' || trimmed.toLowerCase() === 'children') return '??';
  if (trimmed.toLowerCase() === 'n' || trimmed.toLowerCase() === 'no name') return '??';
  
  const words = trimmed.split(' ').filter(n => n.length > 0);
  if (words.length === 0) return '??';
  
  const initials = words
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
  
  return initials || '??';
};

/**
 * Get emoji for poll option based on option text
 */
const getOptionEmoji = (optionText) => {
  if (!optionText) return '';
  const text = optionText.toLowerCase().trim();
  
  // Yes/Going options
  if (text.includes('yes') || text.includes("i'm going") || text.includes('going') || text.includes('attend')) {
    return '✅';
  }
  
  // No options
  if (text.includes('no') || text.includes('not this time') || text.includes('not going')) {
    return '❌';
  }
  
  // Maybe options
  if (text.includes('maybe') || text.includes('still deciding') || text.includes('undecided')) {
    return '🤔';
  }
  
  // Date/time options
  if (text.match(/\d{1,2}\/\d{1,2}/) || text.match(/\d{1,2}:\d{2}/) || text.includes('am') || text.includes('pm') || 
      text.includes('monday') || text.includes('tuesday') || text.includes('wednesday') || text.includes('thursday') || 
      text.includes('friday') || text.includes('saturday') || text.includes('sunday') || text.includes('jan') || 
      text.includes('feb') || text.includes('mar') || text.includes('apr') || text.includes('may') || text.includes('jun') ||
      text.includes('jul') || text.includes('aug') || text.includes('sep') || text.includes('oct') || text.includes('nov') || text.includes('dec')) {
    return 'CLOCK_ICON';
  }
  
  // Default/neutral
  return '⚪';
};

export default function PollCard({ poll, camp, onVote, currentUserVote }) {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  const totalVotes = poll.votes?.length || 0;
  const yesVotes = poll.votes?.filter(v => v.vote === "yes").length || 0;
  const maybeVotes = poll.votes?.filter(v => v.vote === "maybe").length || 0;
  const noVotes = poll.votes?.filter(v => v.vote === "no").length || 0;

  // Check if poll is targeted to specific friends
  const isTargetedPoll = poll.target_friend_emails && poll.target_friend_emails.length > 0;

  // Get camp image URL
  const campImageUrl = camp?.image_url || camp?.image || camp?.program_data?.image_url || camp?.program_data?.image;
  const campLocation = camp?.location || camp?.formatted_address || camp?.general_location || camp?.program_data?.location || camp?.program_data?.formatted_address || camp?.program_data?.general_location;
  const campRating = camp?.rating || camp?.program_data?.rating;

  return (
    <div 
      className="p-0.5 rounded-xl transition-all duration-200 hover:scale-[1.01]"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
      }}
    >
      <Card 
        className={`border backdrop-blur-md transition-all duration-200 hover:shadow-xl rounded-xl shadow-lg ${isDarkTheme ? 'bg-slate-900' : 'bg-white'}`}
        style={{
          borderColor: isDarkTheme ? 'rgba(51, 65, 85, 0.5)' : 'rgba(255, 255, 255, 0.3)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
      {/* Hero Image Section */}
      {campImageUrl && (
        <div className="relative h-40 w-full overflow-hidden rounded-t-xl">
          <img
            src={campImageUrl}
            alt={camp?.name || "Camp"}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div 
            className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center"
            style={{ display: 'none' }}
          >
            <div className="text-6xl">{camp?.emoji || '🏕️'}</div>
          </div>
        </div>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className={`text-lg font-extrabold tracking-tight ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'} mb-1`}>
              {poll.question}
            </CardTitle>
            {camp?.name && (
              <p className={`text-base font-semibold ${isDarkTheme ? 'text-slate-200' : 'text-slate-700'} mb-2`}>
                {camp.name}
              </p>
            )}
            
            {/* Camp Details: Location and Rating */}
            {(campLocation || campRating) && (
              <div className="flex flex-wrap items-center gap-3 mt-2 mb-2">
                {campLocation && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <MapPin className={`w-4 h-4 ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`} />
                    <span className={isDarkTheme ? 'text-slate-300' : 'text-slate-600'}>
                      {campLocation.length > 40 ? `${campLocation.substring(0, 40)}...` : campLocation}
                    </span>
                  </div>
                )}
                {campRating && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <Star className={`w-4 h-4 fill-yellow-400 text-yellow-400 ${isDarkTheme ? 'text-yellow-400' : 'text-yellow-500'}`} />
                    <span className={`font-medium ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                      {Number(campRating).toFixed(1)}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* Show targeting indicator */}
              {isTargetedPoll && (
                <div className="flex items-center gap-1">
                  <Icon name="users" size="xs" color="muted" />
                  <span className={`text-xs ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}>
                    Targeted poll • {poll.target_friend_emails.length} friend{poll.target_friend_emails.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {/* Show carpool interest indicator */}
              {poll.carpool_interest && (
                <Badge className="bg-blue-100/80 backdrop-blur-sm text-blue-700 border border-blue-200/50 text-xs flex items-center gap-1">
                  <Icon name="transport" size="xs" />
                  Carpool Interest
                </Badge>
              )}
            </div>
            
            {/* Show program type if from UserProgram */}
            {camp?.program_type_inferred && (
              <div className="flex items-center gap-1 mt-1">
                <Badge className="bg-blue-100/80 backdrop-blur-sm text-blue-700 border border-blue-200/50 text-xs">
                  {camp.program_type_inferred}
                </Badge>
                {camp.age_range && (
                  <Badge className="bg-gray-100/80 backdrop-blur-sm text-gray-700 border border-gray-200/50 text-xs">
                    Ages: {camp.age_range}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <Badge className="bg-teal-100/80 backdrop-blur-sm text-teal-700 border border-teal-200/50 text-xs">
            <Icon name="users" size="xs" className="mr-1" />
            {totalVotes}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Show brief description for user-created programs */}
        {camp?.brief_description && (
          <p className={`text-sm ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'} leading-relaxed`}>
            {camp.brief_description}
          </p>
        )}

        {/* Show additional details for user-created programs */}
        {(camp?.schedule_dates || camp?.cost_structure) && (
          <div className={`flex flex-wrap gap-2 text-xs ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}>
            {camp.schedule_dates && (
              <span className={`flex items-center gap-1.5 ${isDarkTheme ? 'bg-slate-800/60 border-slate-700/50' : 'bg-slate-100/60 border-slate-200/50'} backdrop-blur-sm border px-2 py-1 rounded`}>
                <Clock className={`w-3 h-3 ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`} />
                {camp.schedule_dates}
              </span>
            )}
            {camp.cost_structure && (
              <span className={`${isDarkTheme ? 'bg-slate-800/60 border-slate-700/50' : 'bg-slate-100/60 border-slate-200/50'} backdrop-blur-sm border px-2 py-1 rounded`}>
                💰 {camp.cost_structure}
              </span>
            )}
          </div>
        )}

        {/* Voting Options - Direct Selection */}
        <div className="space-y-2 mt-4">
          {/* Yes Option */}
          <button
            type="button"
            onClick={() => onVote(poll.id, "yes")}
            className={`w-full text-left rounded-lg p-4 transition-all duration-200 cursor-pointer transform hover:scale-[1.02] active:scale-[0.98] ${
              currentUserVote === "yes"
                ? isDarkTheme
                  ? 'bg-gradient-to-r from-emerald-600/20 to-emerald-500/20 border-2 border-emerald-500 shadow-lg ring-2 ring-emerald-500/30'
                  : 'bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-500 shadow-lg ring-2 ring-emerald-500/30'
                : isDarkTheme
                  ? 'bg-slate-800/50 border border-slate-700/40 hover:bg-slate-800 hover:border-slate-600 hover:shadow-sm'
                  : 'bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 hover:shadow-sm ring-1 ring-transparent hover:ring-brand-200'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  currentUserVote === "yes"
                    ? 'bg-emerald-500 border-emerald-500'
                    : isDarkTheme
                      ? 'border-slate-600 bg-slate-700'
                      : 'border-slate-300 bg-white'
                }`}>
                  {currentUserVote === "yes" && (
                    <Icon name="check" size="xs" className="text-white" />
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-lg flex-shrink-0">✅</span>
                  <span className={`text-sm ${currentUserVote === "yes" ? 'font-semibold' : 'font-medium'} ${
                    isDarkTheme ? 'text-slate-200' : 'text-slate-700'
                  }`}>
                    Yes, I'm going
                  </span>
                </div>
              </div>
              <span className={`text-sm font-semibold flex-shrink-0 ${
                currentUserVote === "yes" 
                  ? 'text-brand-600' 
                  : isDarkTheme ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {yesVotes} {yesVotes === 1 ? 'vote' : 'votes'}
              </span>
            </div>
          </button>

          {/* Maybe Option */}
          <button
            type="button"
            onClick={() => onVote(poll.id, "maybe")}
            className={`w-full text-left rounded-lg p-4 transition-all duration-200 cursor-pointer transform hover:scale-[1.02] active:scale-[0.98] ${
              currentUserVote === "maybe"
                ? isDarkTheme
                  ? 'bg-gradient-to-r from-yellow-600/20 to-yellow-500/20 border-2 border-yellow-500 shadow-lg ring-2 ring-yellow-500/30'
                  : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-500 shadow-lg ring-2 ring-yellow-500/30'
                : isDarkTheme
                  ? 'bg-slate-800/50 border border-slate-700/40 hover:bg-slate-800 hover:border-slate-600 hover:shadow-sm'
                  : 'bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 hover:shadow-sm ring-1 ring-transparent hover:ring-brand-200'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  currentUserVote === "maybe"
                    ? 'bg-yellow-500 border-yellow-500'
                    : isDarkTheme
                      ? 'border-slate-600 bg-slate-700'
                      : 'border-slate-300 bg-white'
                }`}>
                  {currentUserVote === "maybe" && (
                    <Icon name="check" size="xs" className="text-white" />
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-lg flex-shrink-0">🤔</span>
                  <span className={`text-sm ${currentUserVote === "maybe" ? 'font-semibold' : 'font-medium'} ${
                    isDarkTheme ? 'text-slate-200' : 'text-slate-700'
                  }`}>
                    Maybe, still deciding
                  </span>
                </div>
              </div>
              <span className={`text-sm font-semibold flex-shrink-0 ${
                currentUserVote === "maybe" 
                  ? 'text-brand-600' 
                  : isDarkTheme ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {maybeVotes} {maybeVotes === 1 ? 'vote' : 'votes'}
              </span>
            </div>
          </button>

          {/* No Option */}
          <button
            type="button"
            onClick={() => onVote(poll.id, "no")}
            className={`w-full text-left rounded-lg p-4 transition-all duration-200 cursor-pointer transform hover:scale-[1.02] active:scale-[0.98] ${
              currentUserVote === "no"
                ? isDarkTheme
                  ? 'bg-gradient-to-r from-slate-600/20 to-slate-500/20 border-2 border-slate-500 shadow-lg ring-2 ring-slate-500/30'
                  : 'bg-gradient-to-r from-slate-50 to-gray-50 border-2 border-slate-500 shadow-lg ring-2 ring-slate-500/30'
                : isDarkTheme
                  ? 'bg-slate-800/50 border border-slate-700/40 hover:bg-slate-800 hover:border-slate-600 hover:shadow-sm'
                  : 'bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 hover:shadow-sm ring-1 ring-transparent hover:ring-brand-200'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  currentUserVote === "no"
                    ? 'bg-slate-500 border-slate-500'
                    : isDarkTheme
                      ? 'border-slate-600 bg-slate-700'
                      : 'border-slate-300 bg-white'
                }`}>
                  {currentUserVote === "no" && (
                    <Icon name="check" size="xs" className="text-white" />
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-lg flex-shrink-0">❌</span>
                  <span className={`text-sm ${currentUserVote === "no" ? 'font-semibold' : 'font-medium'} ${
                    isDarkTheme ? 'text-slate-200' : 'text-slate-700'
                  }`}>
                    No, not this time
                  </span>
                </div>
              </div>
              <span className={`text-sm font-semibold flex-shrink-0 ${
                currentUserVote === "no" 
                  ? 'text-brand-600' 
                  : isDarkTheme ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {noVotes} {noVotes === 1 ? 'vote' : 'votes'}
              </span>
            </div>
          </button>
        </div>

        {/* Participants */}
        {poll.votes && poll.votes.length > 0 && (
          <div className={`space-y-2 pt-2 border-t ${isDarkTheme ? 'border-slate-700/50' : 'border-slate-100/50'}`}>
            <div className={`flex items-center gap-2 text-xs font-semibold ${isDarkTheme ? 'text-slate-200' : 'text-slate-700'}`}>
              <Icon name="message" size="xs" />
              Who's responded:
            </div>
            <div className="flex flex-wrap gap-1.5">
              {poll.votes.map((vote, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className={`text-xs backdrop-blur-sm border-slate-200/60 bg-slate-50/50 text-slate-700 ${
                    isDarkTheme ? 'border-slate-700/60 bg-slate-800/50 text-slate-300' : ''
                  }`}
                >
                  {vote.user_name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}